import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { bool, func, shape, string } from 'prop-types'
import * as yup from 'yup'
import {
  DialogOverlay,
  PersonBlock,
  Button,
  DialogOverlayFooter,
  TextField,
  useStatusOverlay,
  DialogOverlayActions,
} from '@fs/zion-ui'
import { postBirthDate } from '../../services/services'
import { useAamDispatch, ACTIONS } from '../../state/AamStore'

const textWrapper = css`
  margin: auto;
  width: 196px;
  input {
    text-align: center;
  }
`
const dialogPadding = css`
  padding: 0 20px 20px;
`
const promptText = css`
  padding: 24px 6px;
  text-align: center;
  font-size: 18px;
`
export default function MissingBirthDateDialog({ onUpdate, overlay, person, setYearInfo }) {
  const dispatch = useAamDispatch()
  const { name, surname, displayName, gender, portraitThumbnailLink, lifespan } = person
  const [t] = useTranslation()
  const addYearRef = useRef()
  const [yearQuery, setYearQuery] = useState('')
  const [submitEnabled, setSubmitEnabled] = useState(false)
  const showStatusOverlay = useStatusOverlay()

  const currentYear = new Date().getFullYear()

  // validate input is a year between 1000 & current year
  const validation = yup
    .number()
    .integer()
    .test(
      'no-leading-zeros',
      'Cannot start with 0', // reject leading zeros before casting to int
      (value, context) => context.originalValue && context.originalValue.match(/^[1-9]\d{3}$/)
    )
    .min(1000)
    .max(currentYear)
    .required()

  const validCheck = () => {
    if (yearQuery) {
      if (validation.isValidSync(yearQuery)) {
        setSubmitEnabled(true)
        return null
      }
      setSubmitEnabled(false)
      return t('aam.add-year.enter-birth-year', 'Please enter a birth year')
    }
    setSubmitEnabled(false)
    return null
  }

  const setYear = (e) => setYearQuery(e.target.value.replace(/\D/g, '')) // filter non-digits

  const submitBirthYear = () => {
    try {
      postBirthDate({ ...person, birthDate: yearQuery }).then((res) => {
        if (res.status === 200) {
          dispatch({
            type: ACTIONS.UPDATE_ANCESTOR_BIRTH_YEAR,
            data: {
              year: yearQuery,
              personId: person.personId,
            },
          })
          overlay.close()
          person.dob = yearQuery
          onUpdate(person)
          setYearInfo && setYearInfo(yearQuery)
          setYearQuery('')
        }
      })
    } catch (err) {
      console.error(err)
      showStatusOverlay({
        message: t('aam.add-year.error', 'An error has occurred'),
        type: 'error',
      })
    }
  }

  return (
    <DialogOverlay {...overlay} title={t('aam.add-year.add-birth', 'Add Birth Year')}>
      <div css={dialogPadding}>
        <PersonBlock
          size="lg"
          centered
          name={displayName ?? ''}
          avatarProps={{
            src: portraitThumbnailLink,
            sex: gender.toLowerCase(),
            monogram: `${name?.[0] ?? ''}${surname?.[0] ?? ''}`,
          }}
          details={`${lifespan}`}
        />
        <div css={promptText}>
          {t('aam.add-year.add', 'Looks like we are missing a birth year for this person. Would you like to add it?')}
        </div>
        <div style={{ textAlign: 'center', padding: '4px' }}>{t('aam.add-year.add-year', 'Birth Year')}</div>
        <div css={textWrapper}>
          <TextField
            data-testid="missing-birth-date-dialog-birth-year-text-field"
            maxLength={4}
            value={yearQuery}
            type="text"
            name="birthYear"
            ref={addYearRef}
            onChange={setYear}
            warningValidate={validCheck}
          />
        </div>
      </div>

      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button data-testid="missing-birth-date-dialog-cancel-button" onClick={overlay.close}>
            {t('aam.add-year.cancel', 'Cancel')}
          </Button>
          <Button
            data-testid="missing-birth-date-dialog-save-to-family-search-button"
            type="submit"
            onClick={submitBirthYear}
            emphasis="high"
            disabled={!submitEnabled}
          >
            {t('aam.add-year.save', 'Save to FamilySearch')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

MissingBirthDateDialog.propTypes = {
  onUpdate: func,
  overlay: shape({
    autoFocus: bool,
    close: func,
    focusBackRef: shape({}),
    handleClick: func,
    handleTransitioned: func,
    isOpen: bool,
    open: func,
    targetRef: shape({}),
    transition: bool,
  }),
  person: shape({
    atn: string,
    displayName: string,
    gender: string,
    genderType: string,
    lifespan: string,
    living: bool,
    name: string,
    personId: string,
    surname: string,
  }),
}
